import React, {FormEvent, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {NavigateFunction, useLocation, useNavigate} from 'react-router-dom';
import {loadStripe, StripeElementsOptions} from '@stripe/stripe-js';
import {Elements, PaymentElement, useElements, useStripe} from '@stripe/react-stripe-js';
import {Amount, AmountCurrencyEnum} from 'blink-debit-api-client-node';
import {
    BlinkPayCheckoutOutcome,
    createStripePaymentIntent,
    getBlinkPayCheckoutOutcome,
    startBlinkPayCheckout
} from '../demoBffClientInstance';

// BlinkPay as a Stripe custom payment method (CPM) inside a deferred-intent
// Payment Element. Elements is created before any PaymentIntent exists, so the
// card path creates its intent at submit time. The browser never talks to the
// Blink Debit API; both paths go through the BFF.

const PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
// Stripe dashboard > Settings > Payments > Custom payment methods
const BLINKPAY_CPM_TYPE_ID = process.env.REACT_APP_STRIPE_BLINKPAY_CPM_TYPE_ID;

// loadStripe is called once per page load, outside the component tree, as Stripe recommends.
const stripePromise = PUBLISHABLE_KEY ? loadStripe(PUBLISHABLE_KEY) : null;

const getSearchParam = (search: string, key: string): string | null => {
    return new URLSearchParams(search).get(key);
};

const parseAmount = (value: string | null): Amount | null => {
    const total = parseFloat(value ?? '');
    if (isNaN(total) || total <= 0) {
        return null;
    }
    return {currency: AmountCurrencyEnum.NZD, total: total.toFixed(2)};
};

const toCents = (amount: Amount): number => Math.round(parseFloat(amount.total) * 100);

const CheckoutForm = ({amount}: {amount: Amount}) => {
    const stripe = useStripe();
    const elements = useElements();
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState('');

    // One idempotency key per method per checkout attempt. A double click or a
    // retried request reuses the key, so the BFF never creates two Blink quick
    // payments or two PaymentIntents for one attempt.
    const attemptKeys = useMemo(() => ({
        blinkpay: crypto.randomUUID(),
        stripe: crypto.randomUUID()
    }), []);

    const handleSubmit = async (e: FormEvent): Promise<void> => {
        e.preventDefault();
        if (!stripe || !elements || busy) {
            return;
        }
        setBusy(true);
        setMessage('');
        let redirecting = false;

        try {
            // Validates the form and tells us which method the customer picked.
            const {error, selectedPaymentMethod} = await elements.submit();
            if (error) {
                setMessage(error.message ?? 'Check your payment details and try again.');
                return;
            }

            if (selectedPaymentMethod === BLINKPAY_CPM_TYPE_ID) {
                // The BFF creates a Blink Debit quick payment (gateway flow) and returns the
                // redirect URI. The customer authorises on BlinkPay's hosted page and returns
                // to the redirect URI the BFF configured.
                const {redirectUri} = await startBlinkPayCheckout(amount, attemptKeys.blinkpay);
                redirecting = true;
                window.location.assign(redirectUri);
                return;
            }

            // Cards and every other Stripe-processed method: standard Stripe path.
            const {clientSecret} = await createStripePaymentIntent(amount, attemptKeys.stripe);
            redirecting = true; // success redirects to return_url
            const {error: confirmError} = await stripe.confirmPayment({
                elements,
                clientSecret,
                confirmParams: {return_url: `${window.location.origin}/stripe`}
            });

            // Only reached when confirmation fails immediately (for example a declined card).
            redirecting = false;
            setMessage(confirmError.message ?? 'Your payment was not accepted. Try another payment method.');
        } catch (err) {
            console.error('Error starting Stripe checkout:', err);
            setMessage('We could not start your payment. Try again, or choose another payment method.');
        } finally {
            if (!redirecting) {
                setBusy(false);
            }
        }
    };

    return (
            <form onSubmit={handleSubmit} noValidate>
                <PaymentElement/>
                {message && (
                        <div className="alert alert-danger mt-3" role="alert" data-testid="stripe-message">
                            {message}
                        </div>
                )}
                <button className="btn btn-primary mt-3" type="submit" disabled={busy || !stripe || !elements}
                        aria-busy={busy} data-testid="stripe-submit">
                    Pay ${amount.total}
                </button>
            </form>
    );
};

// Stripe sends the customer back here after confirmPayment with redirect_status
// and payment_intent appended. Query parameters are not authoritative; this only
// shows the customer what happened. The BFF verifies the outcome server-side.
const Outcome = ({status, paymentIntentId, navigate}: {
    status: string,
    paymentIntentId: string | null,
    navigate: NavigateFunction
}) => {
    const succeeded = status === 'succeeded';
    return (
            <div className="container">
                <div className={`alert ${succeeded ? 'alert-success' : 'alert-danger'} d-flex align-items-center`}
                     role="alert">
                    <div className="col-1 d-flex justify-content-center align-items-center">
                        <i className={`bi ${succeeded ? 'bi-check-circle-fill' : 'bi-exclamation-circle-fill'}`}></i>
                    </div>
                    <div className="col text-left">
                        <h3 className="alert-heading">{succeeded ? 'Success' : 'Error'}</h3>
                        <p>Payment {succeeded ? 'successful' : 'unsuccessful'} for payment intent: {paymentIntentId}</p>
                        {!succeeded && <p>Status: {status}</p>}
                    </div>
                </div>
                <button className="btn btn-primary mt-3" onClick={() => navigate('/')}>
                    Back to Shopping Cart
                </button>
            </div>
    );
};

// Blink sends the customer back here with cid (the quick payment ID) and, on failure,
// error. Neither is authoritative: the BFF asks Blink for the outcome and records it
// in Stripe. Once the outcome is final the query string is cleared, so a reload does
// not repeat the check; a pending outcome keeps it, so a reload checks again.
const BlinkPayOutcome = ({quickPaymentId, navigate}: {
    quickPaymentId: string,
    navigate: NavigateFunction
}) => {
    const [result, setResult] = useState<BlinkPayCheckoutOutcome | null>(null);
    const [apiError, setApiError] = useState<string | null>(null);
    const [checking, setChecking] = useState(false);
    const hasFetched = useRef(false);

    const check = useCallback(async (): Promise<void> => {
        setChecking(true);
        setApiError(null);
        try {
            const outcome = await getBlinkPayCheckoutOutcome(quickPaymentId);
            setResult(outcome);
            if (outcome.outcome !== 'pending') {
                window.history.replaceState(null, '', '/stripe');
            }
        } catch (err) {
            setApiError('We could not confirm your payment. Check with your bank before trying again.');
        } finally {
            setChecking(false);
        }
    }, [quickPaymentId]);

    useEffect(() => {
        if (!hasFetched.current) {
            hasFetched.current = true;
            check();
        }
    }, [check]);

    if (!result && !apiError) {
        return <div className="container">Confirming your payment...</div>;
    }

    const paid = result?.outcome === 'paid';
    const pending = result?.outcome === 'pending';
    const alertClass = paid ? 'alert-success' : pending ? 'alert-warning' : 'alert-danger';
    const iconClass = paid ? 'bi-check-circle-fill' : pending ? 'bi-hourglass-split' : 'bi-exclamation-circle-fill';
    const heading = paid ? 'Success' : pending ? 'Processing' : 'Error';

    return (
            <div className="container">
                <div className={`alert ${alertClass} d-flex align-items-center`} role="alert"
                     data-testid="blinkpay-outcome">
                    <div className="col-1 d-flex justify-content-center align-items-center">
                        <i className={`bi ${iconClass}`}></i>
                    </div>
                    <div className="col text-left">
                        <h3 className="alert-heading">{heading}</h3>
                        {apiError && <p>{apiError}</p>}
                        {paid && <p>Payment successful for quick payment: {quickPaymentId}</p>}
                        {pending && <p>Your bank is still processing quick payment {quickPaymentId}.</p>}
                        {result?.outcome === 'failed' && <p>Payment unsuccessful for quick payment: {quickPaymentId}</p>}
                        {result?.paymentRecordId && <p>Stripe Payment Record: {result.paymentRecordId}</p>}
                    </div>
                </div>
                {(pending || apiError) && (
                        <button className="btn btn-secondary mt-3 me-2" onClick={check} disabled={checking}
                                aria-busy={checking}>
                            Check again
                        </button>
                )}
                <button className="btn btn-primary mt-3" onClick={() => navigate('/')}>
                    Back to Shopping Cart
                </button>
            </div>
    );
};

export default function StripeCheckout() {
    const navigate: NavigateFunction = useNavigate();
    const search: string = useLocation().search;
    const redirectStatus = getSearchParam(search, 'redirect_status');
    const quickPaymentId = getSearchParam(search, 'cid');
    const amount = parseAmount(getSearchParam(search, 'amount'));

    if (redirectStatus) {
        return <Outcome status={redirectStatus} paymentIntentId={getSearchParam(search, 'payment_intent')}
                        navigate={navigate}/>;
    }

    if (quickPaymentId) {
        return <BlinkPayOutcome quickPaymentId={quickPaymentId} navigate={navigate}/>;
    }

    if (!amount) {
        return (
                <div className="container">
                    <div className="alert alert-danger" role="alert">No amount to pay. Start from the shopping cart.</div>
                    <button className="btn btn-primary mt-3" onClick={() => navigate('/')}>Back to Shopping Cart</button>
                </div>
        );
    }

    if (!stripePromise || !BLINKPAY_CPM_TYPE_ID) {
        return (
                <div className="container">
                    <div className="alert alert-danger" role="alert">
                        Stripe is not configured. Set REACT_APP_STRIPE_PUBLISHABLE_KEY and
                        REACT_APP_STRIPE_BLINKPAY_CPM_TYPE_ID.
                    </div>
                </div>
        );
    }

    const options: StripeElementsOptions = {
        mode: 'payment',
        amount: toCents(amount), // display only; the BFF decides what is charged
        currency: 'nzd',
        customPaymentMethods: [{
            id: BLINKPAY_CPM_TYPE_ID,
            options: {type: 'static', subtitle: 'Pay directly from your bank'}
        }]
    };

    return (
            <div className="container">
                <h3 className="text-center">Checkout</h3>
                <Elements stripe={stripePromise} options={options}>
                    <CheckoutForm amount={amount}/>
                </Elements>
            </div>
    );
}
